import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { useRealtime } from "@/lib/realtime";
import {
  User, UserPlus, Search, Users, X, Calendar, MapPin, Phone,
  Award, ShieldCheck, Plus, ChevronDown, ChevronRight, Minus, Maximize2,
  UsersRound, UserCheck, TrendingUp, ArrowLeft,
} from "lucide-react";

/**
 * Network Genealogy — full-featured binary tree page (Feb 2026 rebuild).
 *   • 4 KPI cards (Total Downline · Active Members · Left/Right Team SP)
 *   • Search + Level (depth) filter + Status filter + Find Member
 *   • Zoom controls (- % +)
 *   • Rich node cards: avatar · code · name · rank · Personal/Left/Right SP
 *     with status-coloured border (Active green / Inactive amber /
 *     Blocked rose) + Vacant dashed "Add Member" slot.
 *   • LEFT LEG / RIGHT LEG pill labels on the horizontal bar under the root.
 *   • Legend at bottom.
 *   • Right-side sliding member-detail drawer (replaces the modal).
 */

// ---------- helpers -----------------------------------------------------

const STATUS_META = {
  active:   { color: "emerald", dot: "bg-emerald-500", ring: "border-emerald-500" },
  inactive: { color: "amber",   dot: "bg-amber-500",   ring: "border-amber-400"   },
  blocked:  { color: "rose",    dot: "bg-rose-500",    ring: "border-rose-500"    },
  vacant:   { color: "sky",     dot: "border border-sky-400", ring: "border-sky-300" },
};

const RANK_CHIP = {
  Executive:        "bg-sky-100 text-sky-700 border-sky-300",
  "Sr. Executive":  "bg-blue-100 text-blue-700 border-blue-300",
  Silver:           "bg-slate-100 text-slate-700 border-slate-300",
  Gold:             "bg-yellow-100 text-yellow-800 border-yellow-300",
  Pearl:            "bg-pink-100 text-pink-700 border-pink-300",
  Emerald:          "bg-emerald-100 text-emerald-700 border-emerald-300",
  Sapphire:         "bg-indigo-100 text-indigo-700 border-indigo-300",
  Ruby:             "bg-rose-100 text-rose-700 border-rose-300",
  Platinum:         "bg-zinc-100 text-zinc-700 border-zinc-300",
  Diamond:          "bg-cyan-100 text-cyan-700 border-cyan-300",
  Royal:            "bg-purple-100 text-purple-700 border-purple-300",
  "Crown-Ambassador":"bg-amber-100 text-amber-800 border-amber-300",
  "Imperial-Crown": "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300",
  "Global-Crown":   "bg-teal-100 text-teal-700 border-teal-300",
};

function nodeStatus(n) {
  if (!n) return "vacant";
  if (n.blocked) return "blocked";
  if (n.active) return "active";
  return "inactive";
}

function initials(name) {
  return (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "??";
}

const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n || 0);

// Flatten a nested tree into level-by-level rows so we can render a
// balanced binary layout. Always returns 2^level slots per level so that
// vacant seats show up as "Add Member" cards.
function flattenLevels(root, maxLevels) {
  const levels = [];
  let cur = [root];
  for (let i = 0; i < maxLevels; i++) {
    levels.push(cur);
    const next = [];
    let hasChild = false;
    cur.forEach((n) => {
      if (n) {
        next.push(n.left || null);
        next.push(n.right || null);
        if (n.left || n.right) hasChild = true;
      } else {
        next.push(null);
        next.push(null);
      }
    });
    if (i >= 1 && !hasChild) break;
    cur = next;
  }
  return levels;
}

function findParent(root, target) {
  if (!root || !target) return null;
  const stack = [root];
  while (stack.length) {
    const n = stack.pop();
    if (!n) continue;
    if (n.left?.user_code === target) return n.user_code;
    if (n.right?.user_code === target) return n.user_code;
    if (n.left)  stack.push(n.left);
    if (n.right) stack.push(n.right);
  }
  return null;
}

// ---------- node cards --------------------------------------------------

function NodeCard({ node, highlighted, onClick, onLongPress, statusFilter }) {
  const status = nodeStatus(node);
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  // Long-press = 500 ms hold on touch OR mouse-down. Right-click on desktop
  // also triggers the detail panel via onContextMenu below.
  const startLongPress = () => {
    if (!node) return;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onLongPress?.(node.user_code);
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleClick = () => {
    if (longPressFired.current) {          // suppress the click that follows a long-press
      longPressFired.current = false;
      return;
    }
    if (node) onClick?.(node.user_code);
  };
  const handleContextMenu = (e) => {
    if (!node) return;
    e.preventDefault();
    longPressFired.current = true;
    onLongPress?.(node.user_code);
  };

  // Vacant slot — dashed "Add Member" card
  if (!node) {
    return (
      <div
        className="w-[152px] h-[124px] rounded-lg border-2 border-dashed border-sky-300 bg-sky-50/60 flex flex-col items-center justify-center gap-1 hover:bg-sky-100/70 transition"
        data-testid="tree-node-empty"
      >
        <div className="w-7 h-7 rounded-full bg-white grid place-items-center shadow-sm ring-1 ring-sky-200">
          <Plus size={13} className="text-sky-500" />
        </div>
        <div className="text-[9.5px] font-extrabold text-sky-600 uppercase tracking-widest">Add IBA</div>
      </div>
    );
  }

  const dimmed = statusFilter && statusFilter !== "all" && statusFilter !== status;
  const ring = highlighted ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-white shadow-xl scale-[1.02]" : "";
  const borderCls = {
    active:   "border-2 border-emerald-500",
    inactive: "border-2 border-amber-400",
    blocked:  "border-2 border-rose-500",
  }[status];
  const avatarBg = {
    active:   "bg-gradient-to-br from-emerald-500 to-emerald-600",
    inactive: "bg-gradient-to-br from-amber-400 to-amber-500",
    blocked:  "bg-gradient-to-br from-rose-500 to-rose-600",
  }[status] || "bg-sky-500";

  return (
    <button
      type="button"
      data-testid={`tree-node-${node.user_code}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      title="Tap to jump into downline · Long-press or right-click for full details"
      className={`w-[152px] rounded-lg bg-white ${borderCls} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition text-left overflow-hidden ${ring} ${dimmed ? "opacity-40" : ""}`}
    >
      {/* Top row: avatar + code + name + rank badge + status dot */}
      <div className="flex items-start gap-1.5 px-2 pt-2 pb-1 relative">
        <div className={`w-7 h-7 rounded-full ${avatarBg} text-white text-[10px] font-extrabold grid place-items-center shrink-0 ring-2 ring-white shadow`}>
          {node.photo ? (
            <img src={node.photo} alt={node.name} className="w-full h-full object-cover rounded-full" />
          ) : (
            initials(node.name)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-mono text-navy/60 truncate">{node.user_code}</div>
          <div className="text-[11px] font-bold text-navy leading-tight truncate" title={node.name}>{node.name || "—"}</div>
          {node.rank_label && (
            <span className={`inline-block mt-0.5 px-1 py-[1px] rounded text-[8.5px] font-extrabold uppercase tracking-wide border ${RANK_CHIP[node.rank_label] || RANK_CHIP.Executive}`}>
              {node.rank_label}
            </span>
          )}
        </div>
        <span className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${STATUS_META[status].dot}`} />
      </div>
      {node.joined_date && (
        <div className="px-2 text-[9px] text-navy/50 -mt-0.5">Joined: {node.joined_date}</div>
      )}
      {/* SP columns */}
      <div className="mt-1 grid grid-cols-3 border-t border-navy/5 divide-x divide-navy/5 text-center">
        <SpCell label="Personal" value={node.personal_sp} tint="text-navy" />
        <SpCell label="Left"     value={node.left_sp}     tint="text-emerald-700" />
        <SpCell label="Right"    value={node.right_sp}    tint="text-amber-700" />
      </div>
      {/* Chevron */}
      <div className="flex justify-center py-0.5 bg-navy/5">
        <ChevronDown size={12} className="text-navy/40" />
      </div>
    </button>
  );
}

function SpCell({ label, value, tint }) {
  return (
    <div className="py-1">
      <div className="text-[7.5px] uppercase tracking-widest text-navy/50 font-bold">{label}</div>
      <div className={`text-[10.5px] font-extrabold tabular-nums ${tint || "text-navy"}`}>{fmtInt(value)}</div>
    </div>
  );
}

// ---------- main page --------------------------------------------------

export default function DistTree() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const location = useLocation();
  const navigate = useNavigate();
  // Seed rootCode from `?root=SMxxx` in the URL so admins / sponsors can share
  // deep-links straight to any IBA's genealogy view.
  const initialRoot = (new URLSearchParams(location.search).get("root") || "").toUpperCase().trim();
  const [rootCode, setRootCode]     = useState(initialRoot);
  const [tree, setTree]             = useState(null);
  const [depth, setDepth]           = useState(3);
  const [stats, setStats]           = useState(null);
  const [query, setQuery]           = useState("");
  const [statusFilter, setStatus]   = useState("all");
  const [matched, setMatched]       = useState(null);
  const [zoom, setZoom]             = useState(100);
  const [autoFit, setAutoFit]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDL]      = useState(false);
  const [detailError, setDE]        = useState("");
  // History = breadcrumb of the last few IBAs the user drilled into.
  // Kept short (max 4) — the tab persists across page navigation but resets
  // on hard reload, which matches customer expectation of a "session trail".
  const [history, setHistory]       = useState([]);
  const canvasRef = useRef(null);

  // The user's own tree root — enables the "← Back to my tree" chip whenever
  // they're viewing someone else's downline. Read from the auth token so we
  // don't need an extra API round-trip.
  const selfCode = (user?.user_code || "").toUpperCase();
  const viewingOwnTree = !rootCode || rootCode === selfCode;

  // -- data fetchers ----------------------------------------------------
  const fetchTree = useCallback(() => {
    const q = rootCode ? `&root=${encodeURIComponent(rootCode)}` : "";
    api.get(`/dist/tree?depth=${depth}${q}`).then((r) => setTree(r.data)).catch(() => setTree(null));
  }, [depth, rootCode]);

  const fetchStats = useCallback(() => {
    api.get("/dist/tree-stats").then((r) => setStats(r.data || null)).catch(() => setStats(null));
  }, []);

  useEffect(() => { fetchTree();  }, [fetchTree]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    let pending = null;
    const debounced = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => { fetchTree(); fetchStats(); pending = null; }, 400);
    };
    const offs = [subscribe("bv.update", debounced), subscribe("order.created", debounced)];
    return () => { offs.forEach((o) => o()); if (pending) clearTimeout(pending); };
  }, [subscribe, fetchTree, fetchStats]);

  // ESC key closes the centered member modal (a11y + desktop expectation)
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  // -- controls ---------------------------------------------------------
  const findMember = () => {
    const q = query.trim().toUpperCase();
    if (!q || !tree) { setMatched(null); return; }
    const stack = [tree]; const seen = new Set();
    while (stack.length) {
      const n = stack.pop();
      if (!n || seen.has(n.user_code)) continue;
      seen.add(n.user_code);
      if ((n.user_code || "").toUpperCase() === q || (n.name || "").toUpperCase().includes(q)) {
        setRootCode(n.user_code); setMatched(n.user_code); return;
      }
      if (n.left)  stack.push(n.left);
      if (n.right) stack.push(n.right);
    }
    setMatched("__none__");
  };

  // Customer feedback (Feb 2026): clicking a member should jump straight
  // into their downline — no modal, no "View Downline" button. All the
  // extra details (sponsor, KYC, location, etc.) can be viewed elsewhere
  // (Members page / All IBA List) and were distracting on the tree.
  //
  // Long-press (or right-click on desktop) still opens the full details
  // panel — see `openMemberDetails` below — for admins / sponsors who need
  // the extra fields without leaving the tree.
  const openMember = useCallback((code) => {
    if (!code) return;
    setRootCode(code);
    setMatched(code);
    setHistory((h) => {
      // Dedupe + keep the last 4 crumbs. Order = drill-in order (oldest → newest).
      const next = h.filter((c) => c !== code).concat([code]);
      return next.slice(-4);
    });
  }, []);

  // Long-press / right-click hook — re-fetches full IBA detail and shows the
  // centered modal (the ORIGINAL member-detail card). We keep this for the
  // handful of admins / sponsors who still need Sponsor / KYC / Location /
  // Mobile fields without leaving the tree page.
  const openMemberDetails = useCallback((code) => {
    if (!code) return;
    setSelected(code); setDetail(null); setDE("");
    setDL(true);
    api
      .get(`/dist/tree/member/${encodeURIComponent(code)}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setDE(e?.response?.data?.detail || "Could not load member details"))
      .finally(() => setDL(false));
  }, []);

  const jumpToCrumb = useCallback((code) => {
    // Same as openMember but doesn't reshuffle history — clicking an
    // existing crumb should just move the marker to that code, keeping the
    // trail intact so the user can walk back and forth.
    if (!code) return;
    setRootCode(code);
    setMatched(code);
  }, []);

  const closeMember = () => { setSelected(null); setDetail(null); setDE(""); };

  // Keep the URL in sync with the current rootCode so the view is shareable.
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const cur = (search.get("root") || "").toUpperCase();
    const next = (rootCode || "").toUpperCase();
    if (next && next !== cur) {
      search.set("root", next);
      navigate({ pathname: location.pathname, search: `?${search.toString()}` }, { replace: true });
    } else if (!next && cur) {
      search.delete("root");
      const qs = search.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
    }
  }, [rootCode, location.pathname, location.search, navigate]);

  const zoomIn  = () => { setAutoFit(false); setZoom((z) => Math.min(150, z + 10)); };
  const zoomOut = () => { setAutoFit(false); setZoom((z) => Math.max(20,  z - 10)); };
  const zoomFit = () => setAutoFit(true);

  const levels = useMemo(() => (tree ? flattenLevels(tree, depth + 1) : []), [tree, depth]);
  const deepestCount = Math.pow(2, Math.max(0, levels.length - 1));

  // Auto-fit: compute the zoom that makes the whole tree fit in the canvas.
  // We keep cards at their natural size and apply a `transform: scale()` on
  // an outer wrapper that is sized to the SCALED dimensions — so the parent
  // container centres the scaled tree correctly (no overflow, no blank gaps).
  useLayoutEffect(() => {
    if (!autoFit || !canvasRef.current || levels.length === 0) return;
    const CELL_W = 160; // natural per-cell width at 100% zoom
    const natural = deepestCount * CELL_W;
    const compute = () => {
      const cw = canvasRef.current?.clientWidth || 0;
      if (!cw) return;
      // Leave 24px total for canvas padding
      const target = Math.max(20, Math.min(100, Math.floor(((cw - 24) / natural) * 100)));
      setZoom(target);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [autoFit, levels.length, deepestCount]);

  // -- render -----------------------------------------------------------
  return (
    <DashboardLayout
      title="Network Genealogy"
      subtitle="View and manage your complete downline structure"
    >
      {/* Toolbar: Search · Level · Status · Find + Zoom */}
      <div className="bg-white border border-sky-200 rounded-lg mb-5 px-4 py-3 flex flex-wrap items-center gap-3 shadow-sm">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-navy/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && findMember()}
            placeholder="Search by ID or Name"
            data-testid="tree-search"
            className="flex-1 px-2 py-1.5 border border-sky-200 rounded-md text-sm focus:border-sky-500 focus:outline-none"
          />
        </div>
        <select
          value={depth}
          onChange={(e) => setDepth(parseInt(e.target.value, 10))}
          data-testid="tree-depth-select"
          className="px-3 py-1.5 border border-sky-200 rounded-md text-sm font-semibold text-navy bg-white"
        >
          {[2, 3, 4, 5].map((d) => (
            <option key={d} value={d}>Level 1-{d + 1}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="tree-status-filter"
          className="px-3 py-1.5 border border-sky-200 rounded-md text-sm font-semibold text-navy bg-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
          <option value="blocked">Blocked only</option>
        </select>
        <button
          type="button"
          onClick={findMember}
          data-testid="tree-find-btn"
          className="px-4 py-1.5 rounded-md bg-gradient-to-r from-sky-500 to-sky-600 text-white text-xs font-extrabold uppercase tracking-widest shadow hover:brightness-110 flex items-center gap-1.5"
        >
          <Search size={13} /> Find IBA
        </button>
        <div className="ml-auto flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 px-1 py-0.5">
          <button type="button" onClick={zoomOut} data-testid="tree-zoom-out" aria-label="Zoom out" className="p-1 hover:bg-white rounded-full text-sky-700">
            <Minus size={14} />
          </button>
          <span data-testid="tree-zoom-value" className="text-xs font-bold text-sky-700 tabular-nums px-1 min-w-[38px] text-center">{zoom}%</span>
          <button type="button" onClick={zoomIn} data-testid="tree-zoom-in" aria-label="Zoom in" className="p-1 hover:bg-white rounded-full text-sky-700">
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={zoomFit}
            data-testid="tree-zoom-fit"
            aria-label="Fit to screen"
            title={autoFit ? "Auto-fit is ON" : "Fit to screen"}
            className={`p-1 rounded-full ${autoFit ? "bg-sky-500 text-white" : "text-sky-700 hover:bg-white"}`}
          >
            <Maximize2 size={13} />
          </button>
        </div>
      </div>

      {/* Layout: KPI + tree canvas (full width). Clicking any node opens a
       *  centered modal overlay (see below) — the old right-side drawer
       *  was confusing on both desktop and mobile per customer feedback. */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard icon={<UsersRound size={20} />} label="Total Downline" value={fmtInt(stats?.total_downline ?? 0)} tone="sky" />
            <KpiCard icon={<UserCheck size={20} />}  label="Active IBAs"    value={fmtInt(stats?.active_members ?? 0)} tone="emerald" />
            <KpiCard icon={<TrendingUp size={20} />} label="Left Team SP"   value={fmtInt(stats?.left_team_sp ?? 0)}   tone="blue" />
            <KpiCard icon={<TrendingUp size={20} />} label="Right Team SP"  value={fmtInt(stats?.right_team_sp ?? 0)}  tone="amber" />
          </div>

          {/* Navigation strip — "Back to my tree" chip + drill-in breadcrumb */}
          {(!viewingOwnTree || history.length > 1) && (
            <div
              data-testid="tree-nav-strip"
              className="mb-3 flex flex-wrap items-center gap-2 bg-white border border-sky-200 rounded-md px-3 py-2"
            >
              {!viewingOwnTree && (
                <button
                  type="button"
                  onClick={() => { setRootCode(selfCode); setMatched(selfCode); setHistory([selfCode]); }}
                  data-testid="tree-back-to-my-tree"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-50 border border-sky-300 text-sky-700 text-[11px] font-extrabold uppercase tracking-widest hover:bg-sky-100 transition"
                  title="Return to your own tree"
                >
                  <ArrowLeft size={12} /> Back to my tree
                </button>
              )}
              {history.length > 1 && (
                <div className="flex items-center gap-1 flex-wrap ml-auto">
                  <span className="text-[9.5px] uppercase tracking-widest text-navy/50 font-bold">Trail:</span>
                  {history.map((code, i) => (
                    <span key={code + "-" + i} className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => jumpToCrumb(code)}
                        data-testid={`tree-crumb-${code}`}
                        className={`px-2 py-0.5 rounded-full font-mono text-[10.5px] font-extrabold transition ${
                          code === rootCode
                            ? "bg-sky-600 text-white shadow-sm"
                            : "bg-white border border-sky-300 text-sky-700 hover:bg-sky-50"
                        }`}
                      >
                        {code}
                      </button>
                      {i < history.length - 1 && <ChevronRight size={11} className="text-navy/30" />}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tree canvas */}
          <div ref={canvasRef} className="bg-gradient-to-b from-sky-50 to-white border border-sky-200 rounded-lg p-3 md:p-6 overflow-hidden shadow-sm">
            {tree ? (
              (() => {
                const CELL_W = 160;
                const naturalWidth = deepestCount * CELL_W;
                const scale = Math.max(0.2, zoom / 100);
                const scaledWidth = naturalWidth * scale;
                return (
                  <div className="flex justify-center overflow-x-auto">
                    <div style={{ width: `${scaledWidth}px`, minWidth: `${Math.min(scaledWidth, naturalWidth)}px` }}>
                      <div
                        style={{
                          width: `${naturalWidth}px`,
                          transform: `scale(${scale})`,
                          transformOrigin: "top left",
                        }}
                      >
                        {levels.map((row, lvlIdx) => {
                          const maxLvl = levels.length - 1;
                          return (
                            <div key={lvlIdx} className="flex justify-center w-full">
                              {row.map((n, i) => {
                                const cellUnits    = Math.pow(2, maxLvl - lvlIdx);
                                const isChild      = lvlIdx > 0;
                                const isLeftChild  = isChild && i % 2 === 0;
                                const isRightChild = isChild && i % 2 === 1;
                                const isLast       = lvlIdx === maxLvl;
                                return (
                                  <div
                                    key={lvlIdx + "-" + i}
                                    className={`relative flex flex-col items-center ${isChild ? "pt-9" : ""} ${!isLast ? "pb-9" : ""}`}
                                    style={{ width: `${cellUnits * CELL_W}px` }}
                                  >
                                    {/* Downstream vertical + parent joint */}
                                    {!isLast && (
                                      <>
                                        <span className="absolute bottom-0 left-1/2 w-[3px] h-9 -translate-x-1/2 bg-sky-500 rounded-full" />
                                        <span className="absolute -bottom-1 left-1/2 w-2.5 h-2.5 -translate-x-1/2 bg-sky-500 rounded-full shadow-sm ring-2 ring-white" />
                                      </>
                                    )}
                                    {/* Upstream inverted-T pieces */}
                                    {isChild && (
                                      <>
                                        <span className="absolute top-0 left-1/2 w-[3px] h-9 -translate-x-1/2 bg-sky-500 rounded-full" />
                                        <span className="absolute top-[30px] left-1/2 w-2.5 h-2.5 -translate-x-1/2 bg-sky-500 rounded-full shadow-sm ring-2 ring-white" />
                                        {isLeftChild && (
                                          <>
                                            <span className="absolute top-0 left-1/2 h-[4px] w-1/2 bg-sky-500 rounded-full" />
                                            <span className="absolute -top-1 right-0 w-3 h-3 translate-x-1/2 bg-sky-500 rounded-full shadow-md ring-2 ring-white" />
                                          </>
                                        )}
                                        {isRightChild && (
                                          <span className="absolute top-0 right-1/2 h-[4px] w-1/2 bg-sky-500 rounded-full" />
                                        )}
                                        {lvlIdx === 1 && isLeftChild && (
                                          <span className="absolute top-4 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[9px] font-extrabold uppercase tracking-widest shadow z-10">
                                            Left Leg
                                          </span>
                                        )}
                                        {lvlIdx === 1 && isRightChild && (
                                          <span className="absolute top-4 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md bg-amber-500 text-white text-[9px] font-extrabold uppercase tracking-widest shadow z-10">
                                            Right Leg
                                          </span>
                                        )}
                                      </>
                                    )}
                                    <NodeCard
                                      node={n}
                                      highlighted={n && matched === n.user_code}
                                      onClick={openMember}
                                      statusFilter={statusFilter}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="text-sky-600 font-semibold py-10 text-center">Loading tree…</div>
            )}
            {matched === "__none__" && (
              <div className="mt-4 text-xs text-rose-600 text-center font-semibold" data-testid="tree-no-match">
                No match found — try increasing depth or check the ID.
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-4 bg-white border border-sky-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-center gap-6 shadow-sm text-xs">
            <LegendDot color="bg-emerald-500" label="Active" />
            <LegendDot color="bg-amber-500"   label="Inactive" />
            <LegendDot color="bg-rose-500"    label="Blocked" />
            <div className="flex items-center gap-2 text-navy/70 font-semibold">
              <span className="inline-block w-3 h-3 rounded border-2 border-dashed border-sky-400 bg-sky-50" />
              <span>Vacant</span>
            </div>
          </div>
        </div>

        {/* Centered member-detail modal — replaces the old right-side drawer.
         *  Backdrop click + Esc both close (see useEffect for keydown handler).
         *  All existing test IDs are preserved (member-panel, member-panel-close,
         *  member-panel-recenter) so downstream QA scripts keep working. */}
        {selected && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-navy/40 backdrop-blur-sm"
            onClick={closeMember}
            data-testid="member-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label="IBA member details"
          >
            <div
              className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <MemberDetailPanel
                detail={detail}
                loading={detailLoading}
                error={detailError}
                onClose={closeMember}
                onJumpTo={(code) => { closeMember(); setRootCode(code); setMatched(code); }}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ---------- KPI + drawer + legend --------------------------------------

function KpiCard({ icon, label, value, tone }) {
  const bg = {
    sky:     "bg-sky-100 text-sky-700",
    emerald: "bg-emerald-100 text-emerald-700",
    blue:    "bg-blue-100 text-blue-700",
    amber:   "bg-amber-100 text-amber-700",
  }[tone] || "bg-sky-100 text-sky-700";
  return (
    <div className="bg-white border border-sky-100 rounded-lg px-4 py-3 flex items-center gap-3 shadow-sm" data-testid={`tree-kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-10 h-10 rounded-full grid place-items-center ${bg}`}>{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-navy/60 font-bold">{label}</div>
        <div className="text-lg font-extrabold text-navy tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-navy/70 font-semibold">
      <span className={`inline-block w-3 h-3 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}

function MemberDetailPanel({ detail, loading, error, onClose, onJumpTo }) {
  const status = detail ? (detail.active ? "active" : "inactive") : "inactive";
  const avatarBg = {
    active:   "bg-gradient-to-br from-emerald-500 to-emerald-600",
    inactive: "bg-gradient-to-br from-amber-400 to-amber-500",
    blocked:  "bg-gradient-to-br from-rose-500 to-rose-600",
  }[status];
  return (
    <div className="bg-white border border-sky-200 rounded-lg overflow-hidden" data-testid="member-panel">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 flex items-center justify-between">
          <div className="text-sm font-bold flex items-center gap-2">
            <User size={16} /> IBA Details
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="member-panel-close"
            className="text-white/90 hover:text-white p-1 rounded-full hover:bg-white/15"
          >
            <X size={18} />
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sky-600 font-semibold">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-rose-600 font-semibold">{error}</div>
        ) : detail ? (
          <div className="p-4 space-y-3">
            <div className="flex flex-col items-center text-center pb-2">
              <div className={`w-20 h-20 rounded-full ${avatarBg} text-white text-xl font-extrabold grid place-items-center ring-4 ring-white shadow`}>
                {detail.photo ? (
                  <img src={detail.photo} alt={detail.name} className="w-full h-full object-cover rounded-full" />
                ) : (
                  initials(detail.name)
                )}
              </div>
              <div className="mt-2 text-base font-extrabold text-navy">{detail.name || "—"}</div>
              <div className="text-xs font-mono text-navy/60">{detail.user_code}</div>
              <span className={`mt-1.5 inline-block px-2 py-0.5 rounded-full border text-[10px] font-extrabold uppercase tracking-widest ${
                detail.active
                  ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                  : "bg-amber-100 text-amber-700 border-amber-300"
              }`}>
                {detail.active ? "Active" : "Inactive"}
              </span>
            </div>

            <FactRow icon={<User size={13} />}     label="Sponsor" value={detail.sponsor?.name || "—"} secondary={detail.sponsor?.user_code} action={detail.sponsor ? () => onJumpTo(detail.sponsor.user_code) : null} />
            <FactRow icon={<Users size={13} />}    label="Direct IBAs" value={fmtInt(detail.directs_count)} />
            <FactRow icon={<UsersRound size={13} />} label="Team IBAs" value={fmtInt((detail.left_team || 0) + (detail.right_team || 0))} secondary={`L ${fmtInt(detail.left_team || 0)} · R ${fmtInt(detail.right_team || 0)}`} />
            <FactRow icon={<Award size={13} />}    label="Current Rank" value={detail.active ? (detail.rank_label || "Executive") : "—"} />
            <FactRow icon={<Calendar size={13} />} label="Joined"   value={detail.created_at || "—"} />
            <FactRow icon={<ShieldCheck size={13} />} label="Activated" value={detail.activated_at ? String(detail.activated_at).slice(0, 10) : "—"} />
            <FactRow icon={<ShieldCheck size={13} />} label="KYC"    value={(detail.kyc_status || "none").toUpperCase()} />
            <FactRow icon={<MapPin size={13} />}   label="Location" value={[detail.city, detail.state].filter(Boolean).join(", ") || "—"} />
            {detail.phone_masked && (
              <FactRow icon={<Phone size={13} />} label="Mobile" value={detail.phone_masked} />
            )}

            <div className="pt-2 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => onJumpTo(detail.user_code)}
                className="w-full px-4 py-2 rounded-md bg-gradient-to-r from-sky-500 to-sky-600 text-white text-xs font-extrabold uppercase tracking-widest shadow hover:brightness-110"
                data-testid="member-panel-recenter"
              >
                View Downline
              </button>
            </div>
          </div>
        ) : null}
    </div>
  );
}

function FactRow({ icon, label, value, secondary, action }) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-sky-50/60 border border-sky-100">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-6 h-6 rounded-full bg-white grid place-items-center text-sky-600 ring-1 ring-sky-200 shrink-0">{icon}</span>
        <div className="min-w-0">
          <div className="text-[9.5px] uppercase tracking-widest text-navy/50 font-bold">{label}</div>
          <div className="text-sm font-bold text-navy truncate">{value}</div>
          {secondary && <div className="text-[10px] text-navy/50 font-mono">{secondary}</div>}
        </div>
      </div>
      {action && (
        <button
          type="button"
          onClick={action}
          className="px-2 py-0.5 rounded-full bg-white border border-sky-300 text-[9.5px] font-extrabold text-sky-700 uppercase tracking-widest hover:bg-sky-100 shrink-0"
        >
          View
        </button>
      )}
    </div>
  );
}
